import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { LogOut, Zap, Loader2, Inbox, UserCheck, CheckCircle2, ListFilter, Calendar, DollarSign, Flag, Clock, KeyRound, Copy, Users, Gift } from "lucide-react";
import { API, BRAND } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import { useTicketsPolling } from "@/lib/polling";
import { toast } from "sonner";
import { useSeo } from "@/lib/seo";

const tabs = [
  { id: "all", label: "All Tickets", icon: ListFilter },
  { id: "unclaimed", label: "Unclaimed", icon: Inbox },
  { id: "mine", label: "My Tickets", icon: UserCheck },
  { id: "completed", label: "Completed", icon: CheckCircle2 },
  { id: "schedule", label: "Schedule", icon: Calendar },
  { id: "codes", label: "Access Codes", icon: KeyRound },
  { id: "referrals", label: "Referrals", icon: Users },
  { id: "redemptions", label: "Redemptions", icon: Gift },
  { id: "payouts", label: "Payouts", icon: DollarSign },
];

const NON_TICKET_SCOPES = ["schedule", "codes", "referrals", "redemptions", "payouts"];

// "both" (PC+Console combo) is deliberately absent — it's discontinued, and the backend can no
// longer create a ticket for it, so an access code generated with that plan would 500 the moment
// a customer tried to redeem it. Existing codes/tickets with plan "both" still display fine below
// via the raw c.plan fallback.
const PLAN_OPTIONS = [
  { id: "pc", label: "PC" },
  { id: "console", label: "Console" },
];

const STATUSES = ["open", "claimed", "in_progress", "completed"];

const STATUS_COLORS = {
  open: "text-yellow-500 border-yellow-500/40 bg-yellow-500/10",
  claimed: "text-blue-400 border-blue-400/40 bg-blue-400/10",
  in_progress: "text-orange-500 border-orange-500/40 bg-orange-500/10",
  completed: "text-emerald-500 border-emerald-500/40 bg-emerald-500/10",
};

const StatusBadge = ({ status }) => (
  <span className={`text-[10px] font-black uppercase tracking-[0.2em] border px-2 py-0.5 ${STATUS_COLORS[status] || "text-zinc-400 border-zinc-400/40"}`}>
    {status?.replace("_", " ")}
  </span>
);

const timeAgo = (iso) => {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const HOURS = Array.from({length: 24}, (_, i) => `${String(i).padStart(2,"0")}:00`).concat(
  Array.from({length: 24}, (_, i) => `${String(i).padStart(2,"0")}:30`)
).sort();

function SchedulePanel({ token }) {
  const [appointments, setAppointments] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  useEffect(() => {
    axios.get(`${API}/appointments`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setAppointments(r.data))
      .catch(() => {});
  }, [token]);

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const aptsThisMonth = appointments.filter(a => {
    const d = new Date(a.date);
    return d.getFullYear() === year && d.getMonth() === month;
  });

  return (
    <div className="p-6 max-w-3xl">
      <h2 className="text-xl font-black uppercase tracking-tighter mb-2" style={{ fontFamily: "'Orbitron', sans-serif" }}>Schedule</h2>
      <p className="text-zinc-500 text-sm mb-6">All times shown in CST.</p>
      <div className="border border-white/10 bg-[#0a0a0a] p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => setCurrentDate(new Date(year, month-1, 1))} className="text-zinc-400 hover:text-orange-500 px-3 py-1 text-lg">←</button>
          <span className="font-bold text-white">{MONTHS[month]} {year}</span>
          <button onClick={() => setCurrentDate(new Date(year, month+1, 1))} className="text-zinc-400 hover:text-orange-500 px-3 py-1 text-lg">→</button>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {DAYS.map(d => <div key={d} className="text-center text-[10px] text-zinc-500 font-bold uppercase">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {Array(firstDay).fill(null).map((_, i) => <div key={`e${i}`} />)}
          {Array(daysInMonth).fill(null).map((_, i) => {
            const day = i + 1;
            const dateStr = `${year}-${String(month+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
            const dayApts = aptsThisMonth.filter(a => a.date === dateStr);
            return (
              <div key={day} className={`aspect-square flex flex-col items-center justify-center text-sm font-bold rounded-sm ${dayApts.length ? "bg-orange-500/20 border border-orange-500/40" : "text-zinc-600"}`}>
                <span className={dayApts.length ? "text-orange-500" : ""}>{day}</span>
                {dayApts.length > 0 && <span className="text-[8px] text-orange-500">{dayApts.length} apt</span>}
              </div>
            );
          })}
        </div>
      </div>
      <div className="space-y-2">
        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-3">All Appointments</p>
        {appointments.length === 0 && <p className="text-zinc-500 text-sm">No appointments yet.</p>}
        {appointments.sort((a,b) => a.date.localeCompare(b.date)).map(a => (
          <div key={a.id} className="flex items-center justify-between bg-[#0a0a0a] border border-white/5 px-4 py-3">
            <div>
              <div className="text-white font-bold text-sm">{a.date} · {a.start} CST</div>
              <div className="text-zinc-500 text-xs">Ticket {a.ticket_id.slice(0,8)} · 30 min session</div>
            </div>
            <Clock className="w-4 h-4 text-orange-500" />
          </div>
        ))}
      </div>
    </div>
  );
}

function AccessCodesPanel({ token }) {
  const [codes, setCodes] = useState([]);
  const [plan, setPlan] = useState("pc");
  const [generating, setGenerating] = useState(false);
  const [justGenerated, setJustGenerated] = useState(null);

  const loadCodes = useCallback(() => {
    axios.get(`${API}/access-codes`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setCodes(r.data))
      .catch(() => {});
  }, [token]);

  useEffect(() => { loadCodes(); }, [loadCodes]);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/access-codes`, { plan }, { headers: { Authorization: `Bearer ${token}` } });
      setJustGenerated(res.data.code);
      loadCodes();
      toast.success("Code generated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to generate code");
    } finally {
      setGenerating(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="p-6 max-w-3xl">
      <h2 className="text-xl font-black uppercase tracking-tighter mb-2" style={{ fontFamily: "'Orbitron', sans-serif" }}>Access Codes</h2>
      <p className="text-zinc-500 text-sm mb-6">
        One-time-use codes that unlock the app without going through Stripe — for reviewers, giveaways, or testing. Each one only works once.
      </p>

      <div className="border border-white/10 bg-[#0a0a0a] p-4 mb-6 flex items-end gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Plan</label>
          <select value={plan} onChange={(e) => setPlan(e.target.value)}
            className="mt-1 block bg-black border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-orange-500">
            {PLAN_OPTIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </div>
        <button onClick={generate} disabled={generating}
          className="bg-orange-500 hover:bg-orange-400 text-black font-black uppercase tracking-[0.2em] text-xs px-6 py-2.5 disabled:opacity-50">
          {generating ? "Generating..." : "Generate Code"}
        </button>
      </div>

      {justGenerated && (
        <div className="border border-orange-500/40 bg-orange-500/5 p-4 mb-6 flex items-center justify-between">
          <span className="font-mono text-lg text-orange-500 font-bold">{justGenerated}</span>
          <button onClick={() => copyCode(justGenerated)}
            className="flex items-center gap-1.5 text-xs text-zinc-300 uppercase tracking-wide border border-white/10 px-3 py-1.5 hover:border-orange-500/50 hover:text-orange-500">
            <Copy className="w-3 h-3" /> Copy
          </button>
        </div>
      )}

      <div className="space-y-2">
        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-3">All Codes ({codes.length})</p>
        {codes.length === 0 && <p className="text-zinc-500 text-sm">No codes generated yet.</p>}
        {codes.map((c) => (
          <div key={c.id} className="flex items-center justify-between bg-[#0a0a0a] border border-white/5 px-4 py-3">
            <div>
              <div className="font-mono text-white font-bold text-sm">{c.code}</div>
              <div className="text-zinc-500 text-xs">
                {PLAN_OPTIONS.find((p) => p.id === c.plan)?.label || c.plan} · by {c.created_by} · {timeAgo(c.created_at)}
              </div>
            </div>
            {c.used ? (
              <span className="text-[10px] text-zinc-500 font-black uppercase text-right">
                Used{c.used_by_email ? ` · ${c.used_by_email}` : ""}
              </span>
            ) : (
              <span className="text-[10px] text-emerald-500 font-black uppercase">Unused</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const REFERRAL_EVENT_STATUSES = ["pending", "qualified", "voided"];
const REWARD_TYPES = ["tweaks", "cash"];

const centsToDollars = (cents) => `$${(cents / 100).toFixed(2)}`;

function ReferralSummaryCard({ token, refreshKey }) {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    axios.get(`${API}/staff/referral-summary`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setSummary(r.data))
      .catch(() => {});
  }, [token, refreshKey]);

  if (!summary) return null;

  const stats = [
    { label: "Referrers", value: summary.total_referrers },
    { label: "Qualified Referrals", value: summary.total_qualified_referrals },
    { label: "Redemptions", value: summary.total_redemptions },
    { label: "Cash Paid", value: centsToDollars(summary.total_cash_paid_cents) },
    { label: "Points Outstanding", value: summary.points_outstanding },
    { label: "Cash Outstanding", value: centsToDollars(summary.cash_outstanding_cents) },
  ];

  return (
    <div className="mb-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {stats.map((s) => (
          <div key={s.label} className="border border-white/10 bg-[#0a0a0a] p-4">
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1">{s.label}</p>
            <p className="text-xl font-black tabular-nums" style={{ fontFamily: "'Orbitron', sans-serif" }}>
              {s.value}
            </p>
          </div>
        ))}
      </div>
      {summary.flagged_referrers > 0 && (
        <div className="mt-3 border border-red-500/40 bg-red-500/5 p-3 flex items-center gap-2">
          <Flag className="w-4 h-4 text-red-400 shrink-0" />
          <p className="text-red-400 text-xs font-bold uppercase tracking-wide">
            {summary.flagged_referrers} referrer{summary.flagged_referrers > 1 ? "s" : ""} flagged for review —
            a void couldn't fully claw back an already-spent balance.
          </p>
        </div>
      )}
    </div>
  );
}

function AdjustReferrerModal({ referrer, token, onClose, onSaved }) {
  const [currency, setCurrency] = useState("points");
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const n = parseInt(delta, 10);
    if (!n || !reason.trim()) {
      toast.error("Delta and reason are required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/staff/referrer/${referrer.id}/adjust`,
        { currency, delta: n, reason: reason.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Balance adjusted");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Adjustment failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0a0a0a] border border-orange-500/30 p-6 w-full max-w-md">
        <h3 className="text-lg font-black uppercase tracking-tighter mb-1" style={{ fontFamily: "'Orbitron', sans-serif" }}>
          Adjust Balance
        </h3>
        <p className="text-zinc-500 text-xs mb-5">
          {referrer.discord_handle} · {referrer.email}
        </p>
        <div className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Currency</label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="mt-1 w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
            >
              <option value="points">Points</option>
              <option value="cash">Cash (cents)</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">
              Delta <span className="text-zinc-600 normal-case">(+ or -, {currency === "cash" ? "cents" : "points"})</span>
            </label>
            <input
              type="number"
              value={delta}
              onChange={(e) => setDelta(e.target.value)}
              placeholder={currency === "cash" ? "e.g. -100" : "e.g. 3"}
              className="mt-1 w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Reason (required)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="mt-1 w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
            />
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-3 border border-white/10 text-zinc-400 text-xs uppercase tracking-[0.2em] font-bold hover:border-white/30">
            Cancel
          </button>
          <button onClick={submit} disabled={saving}
            className="flex-1 px-4 py-3 bg-orange-500 hover:bg-orange-400 text-black text-xs uppercase tracking-[0.2em] font-black disabled:opacity-50">
            {saving ? "Saving..." : "Apply Adjustment"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReferrersTable({ token, refreshKey, onChanged }) {
  const [referrers, setReferrers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adjusting, setAdjusting] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/staff/referrers`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setReferrers(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const toggleSuspend = async (id) => {
    try {
      await axios.post(`${API}/staff/referrer/${id}/suspend`, null, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Status updated");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update status");
    }
  };

  return (
    <div>
      {adjusting && (
        <AdjustReferrerModal
          referrer={adjusting}
          token={token}
          onClose={() => setAdjusting(null)}
          onSaved={() => { load(); onChanged?.(); }}
        />
      )}
      <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-3">Referrers ({referrers.length})</p>
      {loading && <p className="text-zinc-500 text-sm">Loading...</p>}
      {!loading && referrers.length === 0 && <p className="text-zinc-500 text-sm">No referrers yet.</p>}
      <div className="space-y-2">
        {referrers.map((r) => (
          <div key={r.id} className="bg-[#0a0a0a] border border-white/5 px-4 py-3 flex flex-wrap items-center gap-3 justify-between">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-orange-500 font-bold text-sm">{r.code}</span>
                <span className="text-white font-bold text-sm">{r.discord_handle}</span>
                {r.status === "suspended" && (
                  <span className="text-[10px] text-red-400 font-black uppercase border border-red-400/40 px-1.5 py-0.5">Suspended</span>
                )}
                {r.flagged_for_review && (
                  <span className="text-[10px] text-yellow-500 font-black uppercase border border-yellow-500/40 px-1.5 py-0.5">Flagged</span>
                )}
              </div>
              <div className="text-zinc-500 text-xs">{r.email} · {r.reward_type}</div>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <div className="text-right">
                <div className="text-white font-bold tabular-nums">{r.points_balance} pts</div>
                <div className="text-zinc-500 tabular-nums">{centsToDollars(r.cash_balance)}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setAdjusting(r)}
                  className="px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.15em] bg-black border border-white/10 text-zinc-300 hover:border-orange-500/50 hover:text-orange-500">
                  Adjust
                </button>
                <button onClick={() => toggleSuspend(r.id)}
                  className="px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.15em] bg-black border border-white/10 text-zinc-300 hover:border-red-500/50 hover:text-red-400">
                  {r.status === "active" ? "Suspend" : "Reactivate"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReferralEventsTable({ token }) {
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState("");
  const [rewardType, setRewardType] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (status) params.status = status;
    if (rewardType) params.reward_type = rewardType;
    axios.get(`${API}/staff/referrals`, { params, headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setEvents(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token, status, rewardType]);

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Referral Events ({events.length})</p>
        <div className="flex gap-2">
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            className="bg-black border border-white/10 text-white px-2 py-1.5 text-xs focus:outline-none focus:border-orange-500">
            <option value="">All statuses</option>
            {REFERRAL_EVENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={rewardType} onChange={(e) => setRewardType(e.target.value)}
            className="bg-black border border-white/10 text-white px-2 py-1.5 text-xs focus:outline-none focus:border-orange-500">
            <option value="">All types</option>
            {REWARD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>
      {loading && <p className="text-zinc-500 text-sm">Loading...</p>}
      {!loading && events.length === 0 && <p className="text-zinc-500 text-sm">No referral events match.</p>}
      <div className="space-y-2">
        {events.map((e) => (
          <div key={e.id} className="bg-[#0a0a0a] border border-white/5 px-4 py-3 flex flex-wrap items-center justify-between gap-2 text-xs">
            <div>
              <span className="font-mono text-orange-500 font-bold">{e.referrer_code}</span>
              <span className="text-zinc-500"> → </span>
              <span className="text-white">{e.buyer_email}</span>
              <span className="text-zinc-500"> · {e.plan_id}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-white tabular-nums">
                {e.reward_type === "cash" ? centsToDollars(e.cash_amount) : `${e.points_amount} pt`}
              </span>
              <span className={`font-black uppercase tracking-[0.15em] px-2 py-0.5 border ${
                e.status === "qualified" ? "text-emerald-500 border-emerald-500/40" :
                e.status === "voided" ? "text-red-400 border-red-400/40" :
                "text-yellow-500 border-yellow-500/40"
              }`}>{e.status}</span>
              <span className="text-zinc-600">{timeAgo(e.created_at)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReferralsPanel({ token }) {
  const [refreshKey, setRefreshKey] = useState(0);
  return (
    <div className="p-6 max-w-4xl">
      <h2 className="text-xl font-black uppercase tracking-tighter mb-2" style={{ fontFamily: "'Orbitron', sans-serif" }}>Referrals</h2>
      <p className="text-zinc-500 text-sm mb-6">Referrer accounts, balances, and the underlying referral events.</p>
      <ReferralSummaryCard token={token} refreshKey={refreshKey} />
      <ReferrersTable token={token} refreshKey={refreshKey} onChanged={() => setRefreshKey((k) => k + 1)} />
      <ReferralEventsTable token={token} />
    </div>
  );
}

function RedemptionsPanel({ token }) {
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/staff/redemptions`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setRedemptions(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <div className="p-6 max-w-3xl">
      <h2 className="text-xl font-black uppercase tracking-tighter mb-2" style={{ fontFamily: "'Orbitron', sans-serif" }}>Redemptions</h2>
      <p className="text-zinc-500 text-sm mb-6">Free tweaks granted via referral points.</p>
      {loading && <p className="text-zinc-500 text-sm">Loading...</p>}
      {!loading && redemptions.length === 0 && <p className="text-zinc-500 text-sm">No redemptions yet.</p>}
      <div className="space-y-2">
        {redemptions.map((r) => (
          <div key={r.id} className="bg-[#0a0a0a] border border-white/5 px-4 py-3 flex items-center justify-between text-sm">
            <div>
              <div className="text-white font-bold">{r.plan_id}</div>
              <div className="text-zinc-500 text-xs">{r.points_spent} points spent · {timeAgo(r.created_at)}</div>
            </div>
            <span className={`text-[10px] font-black uppercase tracking-[0.15em] px-2 py-0.5 border ${
              r.status === "granted" ? "text-emerald-500 border-emerald-500/40" : "text-yellow-500 border-yellow-500/40"
            }`}>{r.status.replace("_", " ")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PayoutsPanel({ token }) {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/staff/payouts`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setPayouts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <div className="p-6 max-w-3xl">
      <h2 className="text-xl font-black uppercase tracking-tighter mb-2" style={{ fontFamily: "'Orbitron', sans-serif" }}>Payouts</h2>
      <p className="text-zinc-500 text-sm mb-6">
        Cash payout history — empty until Stripe Connect and the monthly payout run are live.
      </p>
      {loading && <p className="text-zinc-500 text-sm">Loading...</p>}
      {!loading && payouts.length === 0 && <p className="text-zinc-500 text-sm">No payouts yet.</p>}
      <div className="space-y-2">
        {payouts.map((p) => (
          <div key={p.id} className="bg-[#0a0a0a] border border-white/5 px-4 py-3 flex items-center justify-between text-sm">
            <div className="text-white font-bold">{centsToDollars(p.amount)}</div>
            <div className="text-zinc-500 text-xs">{timeAgo(p.created_at)}</div>
            <span className={`text-[10px] font-black uppercase tracking-[0.15em] px-2 py-0.5 border ${
              p.status === "sent" ? "text-emerald-500 border-emerald-500/40" :
              p.status === "failed" ? "text-red-400 border-red-400/40" : "text-yellow-500 border-yellow-500/40"
            }`}>{p.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SetAppointmentModal({ ticket, token, onClose, onSaved }) {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("09:00");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!date || !time) { toast.error("Pick a date and time"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/appointments`, { ticket_id: ticket.id, date, start: time }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Appointment set!");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to set appointment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0a0a0a] border border-orange-500/30 p-6 w-full max-w-md">
        <h3 className="text-lg font-black uppercase tracking-tighter mb-1" style={{ fontFamily: "'Orbitron', sans-serif" }}>Set Appointment</h3>
        <p className="text-zinc-500 text-xs mb-5">For {ticket.customer_name || ticket.customer_email} · 30 min · CST</p>
        <div className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)}
              className="mt-1 w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-orange-500" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Start Time (CST)</label>
            <select value={time} onChange={e => setTime(e.target.value)}
              className="mt-1 w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-orange-500">
              {HOURS.map(h => <option key={h} value={h}>{h} CST</option>)}
            </select>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-3 border border-white/10 text-zinc-400 text-xs uppercase tracking-[0.2em] font-bold hover:border-white/30">Cancel</button>
          <button onClick={save} disabled={saving}
            className="flex-1 px-4 py-3 bg-orange-500 hover:bg-orange-400 text-black text-xs uppercase tracking-[0.2em] font-black disabled:opacity-50">
            {saving ? "Saving..." : "Set Appointment"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function StaffDashboard() {
  // No title here on purpose — the effect further down owns document.title so it can prefix an
  // unclaimed-ticket count. This call is only here for the noindex tag.
  useSeo({ path: "/dashboard", noindex: true });

  const { token, user, loading: authLoading, logout } = useAuth();
  const [scope, setScope] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showAptModal, setShowAptModal] = useState(false);
  const navigate = useNavigate();
  const { tickets } = useTicketsPolling(token, NON_TICKET_SCOPES.includes(scope) ? "all" : scope, 3000);
  const prevCount = useRef(0);

  useEffect(() => {
    if (authLoading) return;
    if (!token || !user || user.role !== "staff") navigate("/staff/login");
  }, [token, user, authLoading, navigate]);

  useEffect(() => {
    const unclaimed = tickets.filter(t => t.status === "open").length;
    prevCount.current = unclaimed;
  }, [tickets]);

  const unclaimedCount = useMemo(() => tickets.filter(t => t.status === "open").length, [tickets]);

  useEffect(() => {
    document.title = unclaimedCount > 0 ? `(${unclaimedCount}) Flow Tweaks Staff` : "Flow Tweaks Staff";
    return () => { document.title = "Flow Tweaks"; };
  }, [unclaimedCount]);

  useEffect(() => {
    if (selectedId) {
      const t = tickets.find(x => x.id === selectedId);
      if (t) setSelectedTicket(t);
    }
  }, [tickets, selectedId]);

  const claim = async (id) => {
    try {
      const res = await axios.post(`${API}/tickets/${id}/claim`, null, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Ticket claimed");
      setSelectedId(id);
      setSelectedTicket(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not claim");
    }
  };

  const updateStatus = async (id, status) => {
    setStatusUpdating(true);
    try {
      const res = await axios.patch(`${API}/tickets/${id}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedTicket(res.data);
      toast.success(`Marked ${status.replace("_", " ")}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Status update failed");
    } finally {
      setStatusUpdating(false);
    }
  };

  const markPayment = async () => {
    setActionLoading(true);
    try {
      const res = await axios.patch(`${API}/tickets/${selectedTicket.id}/payment`, null, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedTicket(res.data);
      toast.success("Payment marked as received!");
    } catch (err) {
      toast.error("Failed to mark payment");
    } finally {
      setActionLoading(false);
    }
  };

  const finishTicket = async () => {
    if (!window.confirm("Mark this ticket as complete?")) return;
    setActionLoading(true);
    try {
      const res = await axios.patch(`${API}/tickets/${selectedTicket.id}/finish`, null, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedTicket(res.data);
      toast.success("Ticket completed!");
    } catch (err) {
      toast.error("Failed to finish ticket");
    } finally {
      setActionLoading(false);
    }
  };

  if (authLoading) return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      {showAptModal && selectedTicket && (
        <SetAppointmentModal ticket={selectedTicket} token={token} onClose={() => setShowAptModal(false)} onSaved={() => {}} />
      )}

      <header className="border-b border-white/5 bg-black/80 backdrop-blur-md">
        <div className="px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Zap className="w-5 h-5 text-orange-500 fill-orange-500" />
            <span className="font-black tracking-[0.18em] text-sm uppercase" style={{ fontFamily: "'Orbitron', sans-serif" }}>
              {BRAND.name} · STAFF
            </span>
            {unclaimedCount > 0 && (
              <span className="px-2 py-0.5 bg-orange-500 text-black text-[10px] font-black uppercase tracking-[0.2em]">
                {unclaimedCount} New
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-zinc-500 hidden sm:inline">@{user?.username}</span>
            <button onClick={() => { logout(); navigate("/"); }}
              className="flex items-center gap-2 px-3 py-2 border border-white/10 hover:border-orange-500/40 text-xs uppercase tracking-[0.2em] text-zinc-300 hover:text-orange-500 transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 grid grid-cols-1 md:grid-cols-12 min-h-0">
        <aside className="md:col-span-4 lg:col-span-3 border-r border-white/5 bg-[#070707] flex flex-col min-h-0">
          <nav className="flex md:flex-col border-b border-white/5">
            {tabs.map(t => {
              const Icon = t.icon;
              return (
                <button key={t.id} onClick={() => { setScope(t.id); setSelectedId(null); setSelectedTicket(null); }}
                  className={`flex-1 md:flex-none px-4 py-3 text-left text-[11px] uppercase tracking-[0.2em] font-bold flex items-center gap-2 border-b md:border-b border-white/5 ${
                    scope === t.id ? "bg-black text-orange-500 border-l-2 border-l-orange-500" : "text-zinc-400 hover:text-white hover:bg-black/50"
                  }`}>
                  <Icon className="w-3.5 h-3.5" /> {t.label}
                </button>
              );
            })}
          </nav>

          {!NON_TICKET_SCOPES.includes(scope) && (
            <div className="flex-1 overflow-y-auto">
              {tickets.length === 0 && <div className="p-6 text-center text-zinc-500 text-sm">No tickets here.</div>}
              {tickets.map(t => {
                const active = selectedId === t.id;
                return (
                  <button key={t.id} onClick={() => { setSelectedId(t.id); setSelectedTicket(t); }}
                    className={`w-full text-left px-4 py-3 border-b border-white/5 transition-colors ${active ? "bg-black border-l-2 border-l-orange-500" : "hover:bg-black/50"}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-orange-500">{t.plan_label}</span>
                      <span className="text-[10px] text-zinc-600">{timeAgo(t.created_at)}</span>
                    </div>
                    <div className="text-white font-bold text-sm truncate">{t.customer_name || t.customer_email}</div>
                    <div className="text-zinc-500 text-xs truncate">{t.customer_email}</div>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <StatusBadge status={t.status} />
                      {t.payment_received && <span className="text-[10px] text-emerald-500 font-black uppercase">Paid ✓</span>}
                      {t.appointment && <span className="text-[10px] text-blue-400 font-black uppercase">📅 {t.appointment.date}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </aside>

        <section className="md:col-span-8 lg:col-span-9 flex flex-col min-h-0">
          {scope === "schedule" ? (
            <div className="flex-1 overflow-y-auto">
              <SchedulePanel token={token} />
            </div>
          ) : scope === "codes" ? (
            <div className="flex-1 overflow-y-auto">
              <AccessCodesPanel token={token} />
            </div>
          ) : scope === "referrals" ? (
            <div className="flex-1 overflow-y-auto">
              <ReferralsPanel token={token} />
            </div>
          ) : scope === "redemptions" ? (
            <div className="flex-1 overflow-y-auto">
              <RedemptionsPanel token={token} />
            </div>
          ) : scope === "payouts" ? (
            <div className="flex-1 overflow-y-auto">
              <PayoutsPanel token={token} />
            </div>
          ) : !selectedTicket ? (
            <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">
              Select a ticket to view details.
            </div>
          ) : selectedTicket.status === "open" ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="border border-orange-500/40 bg-[#0a0a0a] p-8 max-w-md text-center">
                <h3 className="text-2xl font-black uppercase tracking-tighter mb-2" style={{ fontFamily: "'Orbitron', sans-serif" }}>Unclaimed ticket</h3>
                <p className="text-zinc-400 text-sm mb-6">Claim this ticket to start working with <span className="text-white">{selectedTicket.customer_name || selectedTicket.customer_email}</span>.</p>
                <button onClick={() => claim(selectedTicket.id)}
                  className="bg-orange-500 hover:bg-orange-400 text-black font-black uppercase tracking-[0.25em] text-xs px-8 py-4 transition-colors">
                  Claim Ticket
                </button>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col p-6 gap-4">
              <div className="border border-white/5 bg-[#0a0a0a] px-4 sm:px-6 py-3 flex flex-wrap gap-2 items-center">
                <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">Status</span>
                {STATUSES.map(s => (
                  <button key={s} disabled={statusUpdating || selectedTicket.status === s}
                    onClick={() => updateStatus(selectedTicket.id, s)}
                    className={`px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] transition-colors ${
                      selectedTicket.status === s ? "bg-orange-500 text-black" : "bg-black border border-white/10 text-zinc-300 hover:border-orange-500/50 hover:text-orange-500"
                    } disabled:opacity-100`}>
                    {s.replace("_", " ")}
                  </button>
                ))}
                <div className="ml-auto flex gap-2 flex-wrap">
                  {selectedTicket.appointment ? (
                    <span className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] bg-blue-500/10 border border-blue-500/30 text-blue-400">
                      <Clock className="w-3 h-3" /> {selectedTicket.appointment.date} {selectedTicket.appointment.start} CST
                    </span>
                  ) : (
                    <button onClick={() => setShowAptModal(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] bg-black border border-white/10 text-zinc-300 hover:border-blue-500/50 hover:text-blue-400 transition-colors">
                      <Calendar className="w-3 h-3" /> Set Appointment
                    </button>
                  )}
                  <button onClick={markPayment} disabled={actionLoading || selectedTicket.payment_received}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] transition-colors ${
                      selectedTicket.payment_received ? "bg-emerald-500/10 text-emerald-500 border border-emerald-500/30 cursor-not-allowed" : "bg-black border border-white/10 text-zinc-300 hover:border-emerald-500/50 hover:text-emerald-500"
                    }`}>
                    <DollarSign className="w-3 h-3" />
                    {selectedTicket.payment_received ? "Paid ✓" : "Payment Received"}
                  </button>
                  <button onClick={finishTicket} disabled={actionLoading || selectedTicket.status === "completed"}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] bg-black border border-white/10 text-zinc-300 hover:border-orange-500/50 hover:text-orange-500 transition-colors disabled:opacity-50">
                    <Flag className="w-3 h-3" /> Finish Ticket
                  </button>
                </div>
              </div>
              <div className="border border-white/5 bg-[#0a0a0a] p-6">
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-3">Customer Info</p>
                <div className="space-y-2 text-sm">
                  <div><span className="text-zinc-500">Name: </span><span className="text-white">{selectedTicket.customer_name || "—"}</span></div>
                  <div><span className="text-zinc-500">Email: </span><span className="text-white">{selectedTicket.customer_email}</span></div>
                  <div><span className="text-zinc-500">Plan: </span><span className="text-white">{selectedTicket.plan_label}</span></div>
                  {selectedTicket.notes && <div><span className="text-zinc-500">Notes: </span><span className="text-white">{selectedTicket.notes}</span></div>}
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
